<template>
    <!-- Dropdown listauksen sulkeminen / klikkauslistan ulkopuolella käsitellä click-outside directiivillä -->
    <div
        v-click-outside="closeSelectList"
        class="dropdownelement"
    >
        <div class="dropdown">
            <div ref="selectedOptions">
                <!-- empty: jos ei ole vielä valintoja niin ei näytetä bordereita koska placeholder inputissa on borderit jo  -->
                <div
                    class="custom-select"
                    :class="{empty: selectedCount === 0}"
                    :disabled="disabled"
                    @click="toggleCheckboxList"
                    @keydown.enter="toggleCheckboxList"
                >
                    <!-- jos ei ole valintoja niin näytetään placeholder input, tähän voi rakentaa jatkossa myös search toiminnon -->
                    <div v-if="selectedCount === 0">
                        <!-- TODO Search -->
                        <input
                            :id="inputId"
                            role="combobox"
                            :disabled="disabled"
                            type="text"
                            class="default empty"
                            :placeholder="getPlaceholder"
                            :aria-label="getAriaLabel"
                            readonly
                        >
                    </div>
                    <div
                        v-else
                        role="combobox"
                        class="selectedValuesContainer"
                        tabindex="0"
                        :aria-label="$t(getAriaLabel)"
                    >
                        <slot
                            v-for="option in selectedCheckboxes"
                            name="selected-option-container"
                        >
                            <div
                                :key="option"
                                :disabled="disabled"
                                class="selectedValues"
                            >
                                {{ option }}
                                <button
                                    :id="'button-' + option"
                                    class="closebutton"
                                    :aria-label="altTextDeselectOption(option)"
                                    @click.stop="addSelectedValue(option)"
                                >
                                    <img
                                        class="close-icon"
                                        :src="crossIcon"
                                        aria-hidden="true"
                                    >
                                </button>
                            </div>
                        </slot>
                    </div>
                </div>
            </div>
            <!-- Alasveto valikko jossa valittavat tiedot checkboxeilla  -->
            <div
                v-show="checkboxListState"
                :id="'div-' + inputId"
                class="dropdown-selectlist"
                :style="{ maxHeight: maxHeight + 'px' }"
            >
                <div
                    v-for="(value, index) in checkboxList"
                    :key="value.identifier"
                >
                    <input
                        :id="'checkbox-' + value.identifier"
                        v-model="selectedCheckboxes"
                        type="checkbox"
                        :value="value.identifier"
                        class="item-container"
                        role="checkbox"
                        :aria-checked="isChecked"
                        @click.stop="addSelectedValue(value.identifier)"
                        @keydown.enter="addSelectedValue(value.identifier)"
                        @change="toggleChecked(index)"
                    >
                    <label
                        :for="'checkbox-' + value.identifier"
                        class="input-label checkbox-input-label"
                    > {{ $t(value.labelText) }}</label>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { Vue, Component, Prop, Emit } from "vue-facing-decorator"
    import DropDownListModel from "@/models/dropdownlistmodel"

    /** Monivalinta komponentti / Multiselect */
    @Component
    export default class CheckboxSelectList extends Vue {
        /** Poistetaan valinta listalta -> palauttaa poistetun arvon */
        @Emit("removed-checkbox")
        removeCheckboxValue(value: string) {
            this.selectedCheckboxes = this.selectedCheckboxes.filter(f => f !== value)
        }

        /** Lisätään valinta listalta -> palauttaa lisätyn arvon */
        @Emit("selected-checkbox")
        addCheckboxValue(value: string) {
            this.selectedCheckboxes.push(value)
        }

        /** Muokataan valintoja -> palauttaa kaikki valitut arvot */
        @Emit("selected-checkboxesChange")
        checkboxValueChange(values: string[]) {
            return values
        }

        /** Näytettävä lista */
        @Prop({ type: Array }) readonly checkboxList: Array<DropDownListModel> | undefined
        /** Komponenttin tunniste */
        @Prop({ type: String }) readonly inputId: string | undefined
        /** Näytettävä placeholder */
        @Prop({ type: String }) readonly placeholder: string | undefined
        /** komponentin disablointi */
        @Prop({ type: Boolean }) readonly disabled: boolean | undefined
        checkboxListState: boolean = false
        selectedCheckboxes: Array<string> = []

        maxHeight: number = 0
        isChecked: boolean = false

        /** jos valikko on auki niin piilotaan placeholder teksti */
        get getPlaceholder(): string {
            if (this.checkboxListState) {
                return ""
            }
            return this.placeholder ?? ""
        }

        get getAriaLabel(): string {
            return this.$t("App.TVJaRadio.RadioList.Lahetinpaikkakunta") ?? ""
        }

        get selectedCount(): number {
            return this.selectedCheckboxes.length ?? 0
        }

        get crossIcon(): string {
            return this.$store.getters.contentServiceUrl + "/Content/icons/svg/ruksi-valkoinen.svg" + "?" + this.$store.getters.contentServiceTimeStamp
        }

        get crossIconAlt(): string | undefined {
            return this.$t("App.Header.CrossIconAlt")
        }

        closeSelectList(): void {
            this.checkboxListState = false
        }

        toggleCheckboxList(): void {
            if (!this.checkboxListState) {
                // set maxheight
                this.maxHeight = window.innerHeight - (this.$el as any).getBoundingClientRect().top - 50
            }
            this.checkboxListState = !this.checkboxListState
        }

        addSelectedValue(value: string): void {
            if (this.selectedCheckboxes.includes(value)) {
                this.removeCheckboxValue(value)
            }
            else {
                this.addCheckboxValue(value)
            }
            // palautetaan kaikki valinnat
            this.checkboxValueChange(this.selectedCheckboxes)
        }

        // Poistaa kaikki valinnat ja emitoi muutokset
        removeAllSelections(): void {
            this.selectedCheckboxes = []
            this.checkboxValueChange([])
        }

        altTextDeselectOption(option): string | undefined {
            return this.$t("App.Delete") + " " + option + ", " + this.$t("App.Button")
        }

        toggleChecked(index) {
            if (this.checkboxList) {
                this.checkboxList[index].checked = !this.checkboxList[index].checked
            }
        }

        async activated(): Promise<void> {
            this.selectedCheckboxes = []
        }
    }

</script>

<style scoped>

    div[disabled=disabled] {
        pointer-events: none;
        color: #939393;
        border-color: #939393;
        background-color: #EDF0F5;
        outline: 1px solid #939393;
    }

    .custom-select {
        position: relative;
        width: 100%;
        height: auto;
        text-align: center;
        outline: 1px solid black;
    }

    .custom-select.empty {
        outline:none
    }
    input.default.empty {
        width: 100%;
    }

    .selectedValuesContainer {
        display: flex;
        flex-direction:row;
        flex-flow: wrap;
        padding-right: 2rem;
    }

    .selectedValues {
        background-color: var(--Traficom-main-color);
        color: #ffffff;
        padding: 0.54rem 1rem 0.54rem 1rem;
        margin: 4px;
        cursor: pointer;
        user-select: none;
    }

    .closebutton:focus {
        outline: 2px solid #ffffff !important;
    }

    .closebutton {
        background: none;
        border: none;
        margin: 0;
        padding: 0;
        cursor: pointer;
        font-weight: 600;
        color: #ffffff;
        right: 70px;
        top: 12px;
    }

    .dropdownelement {
        margin: 0 0 12px 0;
    }
    .dropdown {
        margin: 10px 0px;
        width: 100%;
        position: relative;
    }

    .dropdown-selectlist {
        position: absolute;
        z-index: 1005;
        background-color: white;
        border: 1px solid black;
        padding: 10px;
        width: inherit;
        overflow-y: auto;
        overflow-x: hidden;
    }

    div > div.dropdown::after {
        position: absolute;
        right: 0.625rem;
        top: 0.75rem;
        filter: invert(10%) sepia(27%) saturate(5527%) hue-rotate(205deg) brightness(96%) contrast(106%);
    }

    .checkbox-list-element .container {
        margin: 0;
        padding: 0;
    }

    .checkbox-div {
        margin-top: .3rem;
    }

    input[type="checkbox"] {
        height: 0px;
        margin-top: .5rem;
    }

        input[type="checkbox"].error {
            filter: invert(34%) sepia(114%) saturate(6707%) hue-rotate(324deg) brightness(79%) contrast(70%);
        }

    label.input-label.checkbox-input-label {
        vertical-align: super;
        width: unset;
    }

    .checkboxelement .error-label {
        position: sticky;
    }
</style>
